import React, { useEffect, useRef } from "react";
import { MapPin, X } from "lucide-react";
import "./profile-dialog.css";
import Button from "../../primitives/button";
import { ProfileAvatar } from "../../profile-display";
import { useDialogFocusRestoration } from "../use-dialog-focus";
import { pickChatColor } from "../../../api/profile/chat-color-service";

function readableTextOn(hexColor) {
  const hex = String(hexColor || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 145 ? "#111827" : "#ffffff";
}

export default function ProfileDialog({
  user,
  onClose,
  calculateAge,
  isBlocked = false,
  onToggleBlock,
  blockActionLoading = false,
  onReportUser,
  reportActionLoading = false,
}) {
  const containerRef = useRef(null);
  useDialogFocusRestoration(Boolean(user), containerRef);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  useEffect(() => {
    const bodyStyle = document.body.style;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const previousOverflow = bodyStyle.overflow;
    const previousPosition = bodyStyle.position;
    const previousTop = bodyStyle.top;
    const previousWidth = bodyStyle.width;

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = "100%";

    return () => {
      bodyStyle.overflow = previousOverflow;
      bodyStyle.position = previousPosition;
      bodyStyle.top = previousTop;
      bodyStyle.width = previousWidth;
      window.scrollTo(0, scrollY);
    };
  }, []);

  if (!user) return null;

  const profileChatColor = pickChatColor(user);
  const profileChatColorContrast = readableTextOn(profileChatColor);
  const containerStyle = {
    "--profile-chat-color": profileChatColor,
    "--profile-chat-color-contrast": profileChatColorContrast,
  };

  const name = user?.profileName || user?.displayName || user?.name || user?.email || "User";
  const bio = user?.story || user?.bio || user?.about || "";
  const location = user?.location?.city && user?.location?.state
    ? `${user.location.city}, ${user.location.state}`
    : null;
  const age = user?.demographics?.dateOfBirth
    ? calculateAge(user.demographics.dateOfBirth)
    : null;
  const gender = user?.demographics?.gender || null;
  const ethnicity = user?.demographics?.ethnicity || null;
  const sexualOrientation = user?.demographics?.sexualOrientation || null;
  const occupation =
    user?.preferences?.occupation ||
    user?.occupation ||
    user?.profile?.occupation ||
    "";
  const politicalAffiliation =
    user?.preferences?.politicalAffiliation ||
    user?.politicalAffiliation ||
    user?.profile?.politicalAffiliation ||
    null;
  const religiousAffiliation =
    user?.preferences?.religiousAffiliation ||
    user?.religiousAffiliation ||
    user?.profile?.religiousAffiliation ||
    null;
  const interests = user?.interests || [];

  // Sociopolitical views
  const sociopoliticalViews =
    user?.sociopoliticalAnswers ||
    user?.sociopolitical ||
    user?.profile?.sociopoliticalAnswers ||
    user?.profile?.sociopolitical ||
    {};
  const sociopoliticalQuestions = [
    { id: "immigration", label: "Immigration", leftLabel: "More Open Immigration", rightLabel: "Stricter Border Enforcement" },
    { id: "gunControl", label: "Gun Control", leftLabel: "Stricter Gun Laws", rightLabel: "Expanded Gun Rights" },
    { id: "abortion", label: "Abortion", leftLabel: "Broader Access", rightLabel: "More Restrictions" },
    { id: "climateChange", label: "Climate Change", leftLabel: "Urgent Climate Action", rightLabel: "Lower Climate Priority" },
    { id: "taxPolicy", label: "Tax Policy", leftLabel: "Higher Taxes & Services", rightLabel: "Lower Taxes & Smaller Government" },
    { id: "lawEnforcement", label: "Law Enforcement", leftLabel: "Reallocate Police Funding", rightLabel: "Expand Police Support" },
    { id: "votingIntegrity", label: "Voting Integrity", leftLabel: "Easier Ballot Access", rightLabel: "Stronger Voter ID Rules" },
    { id: "regulatoryPhilosophy", label: "Regulatory Philosophy", leftLabel: "More Federal Regulation", rightLabel: "Less Federal Regulation" },
    { id: "tradePolicy", label: "Trade Policy", leftLabel: "Free Trade Focus", rightLabel: "Tariff Protection Focus" },
    { id: "healthcare", label: "Healthcare", leftLabel: "Public Coverage Focus", rightLabel: "Private Coverage Focus" },
  ];

  const getPositionLabel = (value) => String(value);

  const sociopoliticalEntries = sociopoliticalQuestions
    .map((question) => {
      const rawValue = Number(sociopoliticalViews?.[question.id]);
      if (!Number.isFinite(rawValue)) return null;
      return {
        ...question,
        value: rawValue,
        isOptedOut: rawValue === 0,
      };
    })
    .filter(Boolean);

  // The backend strips sociopoliticalAnswers for members who keep them private
  // and returns sociopoliticalPublic === false. Render an explicit notice in
  // that case so a private profile reads as "hidden" rather than "blank".
  const sociopoliticalIsPrivate =
    (user?.sociopoliticalPublic ?? user?.profile?.sociopoliticalPublic) === false;

  return (
    <div className="profileModal__overlay" onClick={onClose}>
      <div
        className="profileModal__container"
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        style={containerStyle}
      >
        {/* Close Button */}
        <button className="profileModal__close" onClick={onClose} aria-label="Close">
          <X className="profileModal__closeIcon" aria-hidden="true" />
        </button>

        {/* Header with Profile Picture */}
        <div className="profileModal__header">
          <div className="profileModal__avatarWrapper">
            <ProfileAvatar user={user} name={name} />
          </div>
          <div className="profileModal__headerInfo">
            <h2 className="profileModal__name">{name}</h2>
            {location && (
              <p className="profileModal__location">
                <MapPin className="profileModal__locationIcon" aria-hidden="true" />
                {location}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="profileModal__content">
          {/* Bio Section */}
          {bio && (
            <div className="profileModal__section">
              <h3 className="profileModal__sectionTitle">About</h3>
              <p className="profileModal__bio">"{bio}"</p>
            </div>
          )}

          {/* Basic Info */}
          <div className="profileModal__section">
            <h3 className="profileModal__sectionTitle">Basic info</h3>
            <div className="profileModal__infoGrid">
              {age && (
                <div className="profileModal__infoItem">
                  <span className="profileModal__infoLabel">Age:</span>
                  <span className="profileModal__infoValue">{age}</span>
                </div>
              )}
              {gender && (
                <div className="profileModal__infoItem">
                  <span className="profileModal__infoLabel">Gender:</span>
                  <span className="profileModal__infoValue">{gender}</span>
                </div>
              )}
              {ethnicity && (
                <div className="profileModal__infoItem">
                  <span className="profileModal__infoLabel">Ethnicity:</span>
                  <span className="profileModal__infoValue">{ethnicity}</span>
                </div>
              )}
              <div className="profileModal__infoItem">
                <span className="profileModal__infoLabel">Occupation:</span>
                <span className="profileModal__infoValue">
                  {occupation ? occupation : "Not provided"}
                </span>
              </div>
              <div className="profileModal__infoItem">
                <span className="profileModal__infoLabel">Political:</span>
                <span className="profileModal__infoValue">
                  {politicalAffiliation || "Not provided"}
                </span>
              </div>
              <div className="profileModal__infoItem">
                <span className="profileModal__infoLabel">Religious:</span>
                <span className="profileModal__infoValue">
                  {religiousAffiliation || "Not provided"}
                </span>
              </div>
              <div className="profileModal__infoItem">
                <span className="profileModal__infoLabel">Sexual Orientation:</span>
                <span className="profileModal__infoValue">
                  {sexualOrientation || "Not provided"}
                </span>
              </div>
            </div>
          </div>

          {/* Sociopolitical Views */}
          {sociopoliticalIsPrivate ? (
            <div className="profileModal__section">
              <h3 className="profileModal__sectionTitle">Sociopolitical Views</h3>
              <p className="profileModal__privateNotice">
                This member's sociopolitical answers are private.
              </p>
            </div>
          ) : sociopoliticalEntries.length > 0 && (
            <div className="profileModal__section">
              <h3 className="profileModal__sectionTitle">Sociopolitical Views</h3>
              <div className="profileModal__sociopolitical">
                {sociopoliticalEntries.map((question) => {
                  const { value, isOptedOut } = question;
                  return (
                    <div key={question.id} className="profileModal__viewItem">
                      <div className="profileModal__viewHeader">
                        <span className="profileModal__viewLabel">{question.label}</span>
                        <span className="profileModal__viewValue">
                          {isOptedOut ? "Doesn't matter" : getPositionLabel(value)}
                        </span>
                      </div>
                      {!isOptedOut && (
                        <div className="profileModal__viewScale">
                          <span className="profileModal__viewScaleLabel">{question.leftLabel}</span>
                          <div className="profileModal__viewBar">
                            <div
                              className="profileModal__viewIndicator"
                              style={{ left: `${((value - 1) / 6) * 100}%` }}
                            />
                          </div>
                          <span className="profileModal__viewScaleLabel">{question.rightLabel}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Interests */}
          {interests.length > 0 && (
            <div className="profileModal__section">
              <h3 className="profileModal__sectionTitle">Interests</h3>
              <div className="profileModal__interests">
                {interests.map((interest, idx) => (
                  <span key={idx} className="profileModal__interest">
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        {(typeof onReportUser === "function" || typeof onToggleBlock === "function") && (
          <div className="profileModal__footer">
            {typeof onReportUser === "function" && (
              <Button
                variant="outline"
                className="profileModal__closeBtn"
                onClick={onReportUser}
                disabled={reportActionLoading}
              >
                {reportActionLoading ? "Reporting..." : "Report User"}
              </Button>
            )}
            {typeof onToggleBlock === "function" && (
              <Button
                variant="outline"
                className="profileModal__closeBtn"
                onClick={onToggleBlock}
                disabled={blockActionLoading}
              >
                {blockActionLoading
                  ? (isBlocked ? "Unblocking..." : "Blocking...")
                  : (isBlocked ? "Unblock User" : "Block User")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
